import { useEffect, useMemo, useState } from "react";
import { X, Search, Link2, Mail, Phone, MessageSquare, Globe, Edit2, Save, XCircle } from "lucide-react";
import { api, StructureContact, GraphData } from "../lib/api";
import { Spinner } from "./Spinner";
import { InlineAlert } from "./InlineAlert";
import { ContactAvatar } from "./ContactAvatar";
import { AvatarUploader } from "./AvatarUploader";

type ContactUsage = {
  contact: StructureContact;
  nodes: Array<{ id: string; label: string; role?: string; color?: string }>;
};

type ContactBookModalProps = {
  structureId: string;
  isOpen: boolean;
  onClose: () => void;
  onNavigate?: (nodeId: string) => void;
};

const hexToRgba = (hex?: string | null, alpha = 0.2) => {
  if (!hex || typeof hex !== "string") return `rgba(255,255,255,${alpha})`;
  const match = hex.trim().match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!match) return `rgba(255,255,255,${alpha})`;
  const [, r, g, b] = match;
  return `rgba(${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(b, 16)}, ${alpha})`;
};

const buildSignalLink = (value?: string | null) => {
  if (!value) return null;
  const trimmed = value.trim();
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  return `https://signal.me/#p/${encodeURIComponent(trimmed)}`;
};

const buildTelegramLink = (value?: string | null) => {
  if (!value) return null;
  const trimmed = value.trim().replace(/^@/, "");
  if (!trimmed) return null;
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  return `https://t.me/${trimmed}`;
};

export function ContactBookModal({ structureId, isOpen, onClose, onNavigate }: ContactBookModalProps) {
  const [contacts, setContacts] = useState<StructureContact[]>([]);
  const [graph, setGraph] = useState<GraphData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<StructureContact>>({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        await loadAll();
      } catch (err: any) {
        setError(err.message || "Kontakte konnten nicht geladen werden");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, structureId]);

  const loadAll = async () => {
    const [contactRes, graphRes] = await Promise.all([
      api.listContacts(structureId, false),
      api.getGraph(structureId),
    ]);
    setContacts(contactRes.contacts);
    setGraph(graphRes);
  };

  const startEdit = (contact: StructureContact) => {
    setEditingId(contact.id);
    setEditForm({
      name: contact.name,
      role: contact.role || "",
      contact_type: contact.contact_type || "primary",
      email: contact.email || "",
      phone: contact.phone || "",
      signal: contact.signal || "",
      matrix: contact.matrix || "",
      telegram: contact.telegram || "",
      website: contact.website || "",
      notes: contact.notes || "",
      tags: contact.tags || [],
      sort_order: contact.sort_order,
      is_board_member: contact.is_board_member,
      avatar_url: contact.avatar_url || null,
      avatar_meta: contact.avatar_meta || undefined,
    });
    setFormError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
    setSaving(false);
    setFormError(null);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    if (!editForm.name || !editForm.name.trim()) {
      setFormError("Name ist ein Pflichtfeld");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await api.updateContact(structureId, editingId, editForm);
      await loadAll();
      cancelEdit();
    } catch (err: any) {
      setFormError(err.message || "Kontakt konnte nicht gespeichert werden");
    } finally {
      setSaving(false);
    }
  };

  const usage = useMemo<ContactUsage[]>(() => {
    if (!graph) return [];
    const out: ContactUsage[] = [];
    contacts.forEach((c) => {
      const nodes: Array<{ id: string; label: string; role?: string; color?: string }> = [];
      graph.nodes.forEach((n) => {
        const ids = (n as any).contact_ids || [];
        const roles = (n as any).contact_roles || {};
        if (ids.includes(c.id)) {
          nodes.push({ id: n.id, label: n.label, role: roles[c.id], color: (n as any).color });
        }
      });
      out.push({ contact: c, nodes });
    });
    const term = search.trim().toLowerCase();
    const filtered = !term
      ? out
      : out.filter((u) => {
          const values = [
            u.contact.name,
            u.contact.role,
            u.contact.email,
            u.contact.phone,
            u.contact.signal,
            u.contact.telegram,
            u.contact.matrix,
          ]
            .filter(Boolean)
            .map((v) => v!.toString().toLowerCase());
          const nodeHits = u.nodes.some((n) => n.label.toLowerCase().includes(term));
          return values.some((v) => v.includes(term)) || nodeHits;
        });
    return filtered.sort((a, b) => a.contact.name.localeCompare(b.contact.name, "de", { sensitivity: "base" }));
  }, [contacts, graph, search]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose}>
      <div
        className="bg-[var(--color-panel)] text-[var(--color-text)] w-full max-w-4xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-[var(--color-muted)]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Kontakte oder Nodes durchsuchen"
              className="bg-transparent outline-none text-sm"
            />
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--color-panel-2)] rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6">
              <Spinner label="Lade Kontaktbuch..." />
            </div>
          ) : error ? (
            <div className="p-4">
              <InlineAlert
                tone="error"
                title="Kontaktbuch konnte nicht geladen werden"
                message={error}
                actionLabel="Erneut laden"
                onAction={() => {
                  // Trigger reload by resetting state; effect will run on isOpen true
                  setGraph(null);
                  setContacts([]);
                  setLoading(true);
                  setError(null);
                  // Re-run load
                  (async () => {
                    try {
                      const [contactRes, graphRes] = await Promise.all([
                        api.listContacts(structureId, false),
                        api.getGraph(structureId),
                      ]);
                      setContacts(contactRes.contacts);
                      setGraph(graphRes);
                    } catch (err: any) {
                      setError(err.message || "Kontakte konnten nicht geladen werden");
                    } finally {
                      setLoading(false);
                    }
                  })();
                }}
              />
            </div>
          ) : usage.length === 0 ? (
            <div className="p-6 text-sm text-[var(--color-muted)]">Keine Kontakte vorhanden.</div>
          ) : (
            <div className="p-4 space-y-3">
              {usage.map((u) => (
                <div key={u.contact.id} className="border border-[var(--color-border)] rounded-xl p-3 bg-[var(--color-panel-2)]">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <ContactAvatar contact={editingId === u.contact.id ? { ...u.contact, ...editForm } as any : u.contact} size={40} enablePreview />
                      <div className="min-w-0">
                        {editingId === u.contact.id ? (
                          <div className="space-y-1">
                            <input
                              className="w-full rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1 text-sm"
                              value={editForm.name || ""}
                              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                              placeholder="Name"
                            />
                            <input
                              className="w-full rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1 text-xs"
                              value={editForm.role || ""}
                              onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                              placeholder="Rolle"
                            />
                          </div>
                        ) : (
                          <>
                            <div className="font-semibold text-sm truncate">{u.contact.name}</div>
                            {u.contact.role && <div className="text-xs text-[var(--color-muted)] truncate">{u.contact.role}</div>}
                          </>
                        )}
                      </div>
                      {u.contact.is_board_member && editingId !== u.contact.id && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full border bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-300 dark:border-purple-700 whitespace-nowrap">
                          Vorstand
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-[11px] text-[var(--color-muted)] whitespace-nowrap">{u.nodes.length} Verwendungen</div>
                      {editingId === u.contact.id ? (
                        <>
                          <button
                            className="btn btn-secondary btn-xs"
                            onClick={cancelEdit}
                            disabled={saving}
                            title="Abbrechen"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                          <button
                            className="btn btn-primary btn-xs"
                            onClick={saveEdit}
                            disabled={saving}
                            title="Speichern"
                          >
                            {saving ? <Spinner size="xs" /> : <Save className="w-4 h-4" />}
                          </button>
                        </>
                      ) : (
                        <button
                          className="btn btn-secondary btn-xs"
                          onClick={() => startEdit(u.contact)}
                          title="Kontakt bearbeiten"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {editingId === u.contact.id && (
                    <div className="mt-3 space-y-3">
                      <AvatarUploader
                        name={editForm.name || u.contact.name}
                        value={(editForm.avatar_url as string | null) ?? u.contact.avatar_url ?? null}
                        meta={editForm.avatar_meta || u.contact.avatar_meta}
                        onChange={(avatar_url, avatar_meta) => setEditForm({ ...editForm, avatar_url, avatar_meta })}
                        disabled={saving}
                      />

                      <div className="grid gap-2 sm:grid-cols-2 text-sm">
                        <input
                          type="email"
                          className="rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1"
                          value={editForm.email || ""}
                          onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                          placeholder="E-Mail"
                          disabled={saving}
                        />
                        <input
                          type="tel"
                          className="rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1"
                          value={editForm.phone || ""}
                          onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                          placeholder="Telefon"
                          disabled={saving}
                        />
                        <input
                          type="text"
                          className="rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1"
                          value={editForm.signal || ""}
                          onChange={(e) => setEditForm({ ...editForm, signal: e.target.value })}
                          placeholder="Signal"
                          disabled={saving}
                        />
                        <input
                          type="text"
                          className="rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1"
                          value={editForm.telegram || ""}
                          onChange={(e) => setEditForm({ ...editForm, telegram: e.target.value })}
                          placeholder="Telegram"
                          disabled={saving}
                        />
                        <input
                          type="text"
                          className="rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1"
                          value={editForm.matrix || ""}
                          onChange={(e) => setEditForm({ ...editForm, matrix: e.target.value })}
                          placeholder="Matrix"
                          disabled={saving}
                        />
                        <input
                          type="url"
                          className="rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1"
                          value={editForm.website || ""}
                          onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                          placeholder="Website"
                          disabled={saving}
                        />
                      </div>

                      <textarea
                        className="w-full rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1 text-sm"
                        value={editForm.notes || ""}
                        onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                        placeholder="Notizen"
                        disabled={saving}
                        rows={3}
                      />

                      <label className="inline-flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          className="rounded"
                          checked={editForm.is_board_member || false}
                          onChange={(e) => setEditForm({ ...editForm, is_board_member: e.target.checked })}
                          disabled={saving}
                        />
                        Vorstandskontakt
                      </label>

                      {formError && (
                        <InlineAlert tone="error" message={formError} dismissible onClose={() => setFormError(null)} />
                      )}
                    </div>
                  )}

                  {editingId !== u.contact.id && (
                    <>
                      {u.nodes.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {(expandedNodes[u.contact.id] ? u.nodes : u.nodes.slice(0, 5)).map((n) => (
                            <button
                              key={n.id}
                              type="button"
                              onClick={() => onNavigate?.(n.id)}
                              className="group inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border transition-colors"
                              style={{
                                background: hexToRgba(n.color, 0.18),
                                color: n.color || "var(--color-text)",
                                borderColor: hexToRgba(n.color, 0.3),
                              }}
                            >
                              <span
                                className="w-2.5 h-2.5 rounded-full border border-[var(--color-border)]"
                                style={{ background: n.color || "var(--color-panel-2)" }}
                                title={n.label}
                              />
                              <Link2 className="w-3 h-3 opacity-80" />
                              <span className="truncate">{n.label}</span>
                            </button>
                          ))}
                          {u.nodes.length > 5 && (
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedNodes((prev) => ({
                                  ...prev,
                                  [u.contact.id]: !prev[u.contact.id],
                                }))
                              }
                              className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-panel)] text-[var(--color-muted)] hover:text-[var(--color-text)] transition-colors"
                            >
                              {expandedNodes[u.contact.id] ? "Weniger" : `… ${u.nodes.length - 5} mehr`}
                            </button>
                          )}
                        </div>
                      )}
                      {(u.contact.email ||
                        u.contact.phone ||
                        u.contact.signal ||
                        u.contact.matrix ||
                        u.contact.telegram ||
                        u.contact.website ||
                        (u.contact.tags && u.contact.tags.length > 0)) && (
                        <div className="mt-3 p-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-panel)] space-y-3">
                          {(u.contact.email ||
                            u.contact.phone ||
                            u.contact.signal ||
                            u.contact.matrix ||
                            u.contact.telegram ||
                            u.contact.website) && (
                            <div className="grid gap-2 sm:grid-cols-2 text-sm">
                              {u.contact.email && (
                                <a
                                  href={`mailto:${u.contact.email}`}
                                  className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-[var(--color-panel-2)] transition-colors min-w-0 w-full"
                                >
                                  <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                                    <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                  </div>
                                  <div className="min-w-0 w-full">
                                    <div className="text-[11px] uppercase tracking-wide text-[var(--color-muted)]">E-Mail</div>
                                    <div className="truncate text-[var(--color-text)] w-full">{u.contact.email}</div>
                                  </div>
                                </a>
                              )}
                              {u.contact.phone && (
                                <a
                                  href={`tel:${u.contact.phone}`}
                                  className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-[var(--color-panel-2)] transition-colors min-w-0 w-full"
                                >
                                  <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                                    <Phone className="w-4 h-4 text-green-600 dark:text-green-400" />
                                  </div>
                                  <div className="min-w-0 w-full">
                                    <div className="text-[11px] uppercase tracking-wide text-[var(--color-muted)]">Telefon</div>
                                    <div className="truncate text-[var(--color-text)] font-mono w-full">{u.contact.phone}</div>
                                  </div>
                                </a>
                              )}
                              {u.contact.signal && (
                                <a
                                  href={buildSignalLink(u.contact.signal) || undefined}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-[var(--color-panel-2)] transition-colors min-w-0 w-full"
                                >
                                  <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
                                    <MessageSquare className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                  </div>
                                  <div className="min-w-0 w-full">
                                    <div className="text-[11px] uppercase tracking-wide text-[var(--color-muted)]">Signal</div>
                                    <div className="truncate text-[var(--color-text)] font-mono w-full">{u.contact.signal}</div>
                                  </div>
                                </a>
                              )}
                              {u.contact.telegram && (
                                <a
                                  href={buildTelegramLink(u.contact.telegram) || undefined}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-[var(--color-panel-2)] transition-colors min-w-0 w-full"
                                >
                                  <div className="w-8 h-8 rounded-full bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center">
                                    <MessageSquare className="w-4 h-4 text-sky-600 dark:text-sky-400" />
                                  </div>
                                  <div className="min-w-0 w-full">
                                    <div className="text-[11px] uppercase tracking-wide text-[var(--color-muted)]">Telegram</div>
                                    <div className="truncate text-[var(--color-text)] font-mono w-full">
                                      @{u.contact.telegram.replace(/^@/, "")}
                                    </div>
                                  </div>
                                </a>
                              )}
                              {u.contact.matrix && (
                                <div className="flex items-center gap-2 px-2 py-1 rounded-lg min-w-0 w-full">
                                  <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
                                    <MessageSquare className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                  </div>
                                  <div className="min-w-0 w-full">
                                    <div className="text-[11px] uppercase tracking-wide text-[var(--color-muted)]">Matrix</div>
                                    <div className="truncate text-[var(--color-text)] font-mono w-full">{u.contact.matrix}</div>
                                  </div>
                                </div>
                              )}
                              {u.contact.website && (
                                <a
                                  href={u.contact.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-[var(--color-panel-2)] transition-colors min-w-0 w-full"
                                >
                                  <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                                    <Globe className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                  </div>
                                  <div className="min-w-0 w-full">
                                    <div className="text-[11px] uppercase tracking-wide text-[var(--color-muted)]">Website</div>
                                    <div className="truncate text-[var(--color-text)] w-full">{u.contact.website}</div>
                                  </div>
                                </a>
                              )}
                            </div>
                          )}

                          {u.contact.tags && u.contact.tags.length > 0 && (
                            <div className="flex items-start gap-2">
                              <span className="text-[11px] uppercase tracking-wide text-[var(--color-muted)] mt-1">Tags:</span>
                              <div className="flex flex-wrap gap-1">
                                {u.contact.tags.map((tag, idx) => (
                                  <span
                                    key={idx}
                                    className="px-2 py-1 rounded-full text-xs border border-[var(--color-border)] bg-[var(--color-panel-2)] text-[var(--color-text)]"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
